import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../../components/Firebase Functions/firebase';
import { AuthContext } from '../../components/hooks/AuthProvider';
import EditJob from './Edit Job/EditJob';
import EmailSender from './../Email Functions/Email Sender/EmailSender';
import './jobDetails.css';

const JobDetails = () => {
  const { id } = useParams(); // Get job ID from URL
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext); // Access user role
  const [jobData, setJobData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const storage = getStorage();

  useEffect(() => {
    const jobRef = doc(db, 'Services', id);

    // Listen for real-time updates to this specific job
    const unsubscribe = onSnapshot(jobRef, async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const job = docSnapshot.data();

        // Process services field to ensure it's an array
        const servicesArray = typeof job.services === 'string'
          ? job.services.split(',').map((s) => s.trim())
          : Array.isArray(job.services) ? job.services : [];

        // Update image URLs if they exist
        const imageKeys = [
  'image',
  'secondImage',
  'thirdImage',
  'fourthImage',
  'fifthImage',
  'sixthImage',
  'seventhImage',
  'eighthImage'
];

const updatedImageUrls = {};

for (const key of imageKeys) {
  const path = job.imageUrls?.[key];
  if (path) {
    try {
      const imageRef = ref(storage, path);
      const url = await getDownloadURL(imageRef);
      updatedImageUrls[key] = url;
    } catch (error) {
      console.error(`Error loading image ${key}:`, error);
      updatedImageUrls[key] = null;
    }
  }
}


        setJobData({
          ...job,
          id,
          services: servicesArray,
          imageUrls: updatedImageUrls,
        });
      }
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, [id]);

  const handleDeleteJob = async () => {
    if (window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      try {
        const jobRef = doc(db, 'Services', id);
        await deleteDoc(jobRef);
        alert('Job deleted successfully.');
        navigate('/job-list'); // Redirect to job list after deletion
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Failed to delete job: ' + error.message);
      }
    }
  };

  const handleSave = async (updatedData) => {
    try {
      const docRef = doc(db, 'Services', id);
      await updateDoc(docRef, {
        ...updatedData,
        services: updatedData.services.join(', '),
      });
      setIsEditing(false); // Exit edit mode
    } catch (error) {
      console.error('Error saving job data:', error);
    }
  };

  const handleEmailClick = () => {
    setIsEmailModalOpen(true);
  };

  if (loading) return <div>Loading...</div>;
  if (!jobData) return <div>No data found for this job.</div>;

  return (
    <div className="job-details-container">
      <div className="job-details-header">
        <h1>Job Details</h1>
        <button onClick={() => navigate('/job-list')} className="back-button">
          ← Back to Services Needing Approval
        </button>
      </div>

      {isEditing ? (
        <EditJob
          jobData={jobData}
          onSave={handleSave}
          onClose={() => setIsEditing(false)}
        />
      ) : (
        <>
          <div className="details-section">
            <p><strong>Supervisor:</strong> {jobData.supervisorName}</p>
            <p><strong>Property:</strong> {jobData.property} {jobData.customPropertyName ? ` ( ${jobData.customPropertyName})` : ''}</p>
            <p><strong>Services:</strong></p>
            <ul>
              {jobData.services.map((service, index) => (
                <li key={index} className="service-item">
                  {service}
                </li>
              ))}
            </ul>
            <p className="job-date"><strong>Date:</strong> {jobData.date}</p>
            <p className="notes-section"><strong>Notes:</strong> {jobData.notes}</p>

            {/* Image Grid */}
            <div className="image-grid">
  {[
    'image',
    'secondImage',
    'thirdImage',
    'fourthImage',
    'fifthImage',
    'sixthImage',
    'seventhImage',
    'eighthImage'
  ].map((key) => {
    const url = jobData.imageUrls?.[key];
    return url ? (
      <div key={key} className="image-container">
        <img
          src={url}
          alt={`Job image ${key}`}
          className="job-image"
        />
      </div>
    ) : null;
  })}
</div>

          </div>

          {/* Action Buttons */}
          <div className="job-details-buttons">
            <button className="send-email-button" onClick={handleEmailClick}>
              Send Email
            </button>
            <button className="edit-job-button" onClick={() => setIsEditing(true)}>
              Edit Job
            </button>
            {currentUser?.role === 'admin' && (
              <button className="delete-job-button" onClick={handleDeleteJob}>
                Delete Job
              </button>
            )}
          </div>
        </>
      )}

      {isEmailModalOpen && (
        <EmailSender
          selectedJob={jobData} // Pass current job data to EmailSender
          onClose={() => setIsEmailModalOpen(false)}
        />
      )}
    </div>
  );
};

export default JobDetails;
